import { useMemo } from 'react';

import {
	AnswerReveal,
	PointsRow,
	StandardPatternBody,
} from '@quizzach/components';
import { IQuestion } from '@quizzach/interfaces';
import { analyzeConnectionQuestion } from '@quizzach/utils';
import {
	OptCols,
	OptList,
	QuestionText,
	QuestionContainer,
} from '@quizzach/play-styles';

interface IConnectionQuestionProps {
	color: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
}

interface IConnectionQuestionAnswerProps {
	questionContainerColor: string;
	answerContainerColor: string;
	currentQuestionNumber: number;
	currentQuestion: IQuestion;
	totalQuestions: number;
	hideQuestion?: boolean;
}

const ConnectionColumns = ({
	currentQuestion,
}: {
	currentQuestion: IQuestion;
}) => {
	const cq = currentQuestion.connection_question;
	if (!cq) return null;
	const letters: Array<[string, string]> = [
		['a', cq.a],
		['b', cq.b],
		['c', cq.c],
		['d', cq.d],
	];
	const numbers: Array<[string, string]> = [
		['1', cq.n1],
		['2', cq.n2],
		['3', cq.n3],
		['4', cq.n4],
	];
	return (
		<OptCols>
			{[letters, numbers].map((column, index) => (
				<OptList key={index}>
					{column.map(([label, html]) => (
						<div className='opt' key={label}>
							<span className='L'>{label}</span>
							<span className='t' dangerouslySetInnerHTML={{ __html: html }} />
						</div>
					))}
				</OptList>
			))}
		</OptCols>
	);
};

const connectionAnswer = (
	currentQuestion: IQuestion,
	withPairTexts = false
) => {
	const cq = currentQuestion.connection_question;
	if (!cq) return '';
	if (!withPairTexts) {
		return `a${cq.answer_a} · b${cq.answer_b} · c${cq.answer_c} · d${cq.answer_d}`;
	}
	// QAQA hides the question columns above the answer, so the bare
	// letter-number pairs carry their actual texts along.
	const numberTexts: Record<string, string> = {
		1: cq.n1,
		2: cq.n2,
		3: cq.n3,
		4: cq.n4,
	};
	return (
		[
			['a', cq.a, cq.answer_a],
			['b', cq.b, cq.answer_b],
			['c', cq.c, cq.answer_c],
			['d', cq.d, cq.answer_d],
		] as const
	)
		.map(
			([letter, text, matched]) =>
				`<span class='pair'>${letter}${matched} <span class='pair-what'>${text} – ${
					numberTexts[String(matched)] ?? ''
				}</span></span>`
		)
		.join('');
};

export const ConnectionQuestionMobile = (props: IConnectionQuestionProps) => {
	const { color, currentQuestion } = props;
	const analysis = useMemo(
		() => analyzeConnectionQuestion(currentQuestion),
		[currentQuestion]
	);

	if (analysis.pattern || analysis.note || analysis.kicker) {
		return (
			<>
				<StandardPatternBody analysis={analysis} color={color} />
				<QuestionContainer color={color}>
					<ConnectionColumns currentQuestion={currentQuestion} />
					<PointsRow
						points={currentQuestion.points}
						explanation={currentQuestion.points_explanation}
					/>
				</QuestionContainer>
			</>
		);
	}

	return (
		<QuestionContainer color={color}>
			<QuestionText>{analysis.questionHtml}</QuestionText>
			<ConnectionColumns currentQuestion={currentQuestion} />
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</QuestionContainer>
	);
};

export const ConnectionQuestionAnswerMobile = (
	props: IConnectionQuestionAnswerProps
) => {
	const {
		questionContainerColor,
		currentQuestionNumber,
		currentQuestion,
		hideQuestion,
	} = props;
	const analysis = useMemo(
		() => analyzeConnectionQuestion(currentQuestion),
		[currentQuestion]
	);

	return (
		<>
			{!hideQuestion &&
			(analysis.pattern || analysis.note || analysis.kicker) ? (
				<>
					<StandardPatternBody
						analysis={analysis}
						color={questionContainerColor}
						dim
					/>
					<QuestionContainer color={questionContainerColor}>
						<ConnectionColumns currentQuestion={currentQuestion} />
					</QuestionContainer>
				</>
			) : (
				<QuestionContainer color={questionContainerColor}>
					{!hideQuestion && (
						<>
							<QuestionText>{analysis.questionHtml}</QuestionText>
							<ConnectionColumns currentQuestion={currentQuestion} />
						</>
					)}
				</QuestionContainer>
			)}
			<AnswerReveal
				revealKey={currentQuestionNumber}
				answerHtml={connectionAnswer(currentQuestion, hideQuestion)}
				explanation={currentQuestion.connection_question?.explanation}
			/>
			<PointsRow
				points={currentQuestion.points}
				explanation={currentQuestion.points_explanation}
			/>
		</>
	);
};
